import React, { useContext } from 'react';
import { Box, ResponsiveContext, Text, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { IconChevronRight } from '@tabler/icons-react';
import styled from 'styled-components';
import Button from './Button';
import Icon from './Icon';
import ChartCard from './charts/ChartCard';

// Everything on a page that asks the merchant to act, in one card instead of a banner each.
// The dashboard and the customer overview use it. See "Banners, tasks and facts" in CLAUDE.md.
// A task is { key, icon, lead?, text, action, onClick, attention?, details? }. `lead` is
// the part in bold, usually the count. `attention` marks money or invoices that are stuck: those
// rows come first and their icon sits on the warning color. Nothing to do, no card. No dismiss:
// the backend clears a notification once its problem is fixed, and one click target per row
// means nobody hides a task when they meant to open it.

// The whole row is the button, tinted on hover like menus and list rows
const Row = styled(Button)`
    display: block;
    flex: 1;
    min-width: 0;
    border-radius: 4px;
    &:hover { background: ${(props) => props.$hover}; }
`;

// How many are waiting, beside the title, so the card says it before anything is read
export const TaskCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: 2px;
    min-width: 22px;
    height: 22px;
    margin-left: 10px;
    padding: 0 7px;
    border-radius: 11px;
    font-size: 13px;
    font-weight: 600;
    background: ${(props) => props.$bg};
`;

// The warning color at a fifth: light enough for a dark icon on it, warm enough to read as stuck.
// For anything that counts work holding up money, here and on the lists the rows open.
export const warningTint = (theme) => `${normalizeColor('status-warning', theme)}33`;

const TaskList = ({ tasks, section }) => {
    const theme = useContext(ThemeContext);
    const color = (name) => normalizeColor(name, theme);
    // On a phone the sentence needs the width, so a row ends in its chevron alone
    const isPhone = useContext(ResponsiveContext) === 'small';
    if (!tasks.length) return null;

    const sorted = [...tasks.filter((task) => task.attention), ...tasks.filter((task) => !task.attention)];
    const urgent = tasks.some((task) => task.attention);
    const tint = warningTint(theme);

    return (
        <ChartCard title={<>To do<TaskCount $bg={urgent ? tint : color('accent')}>{tasks.length}</TaskCount></>}>
            <Box gap="xxsmall" margin={isPhone ? { top: 'xsmall' } : undefined}>
                {sorted.map((task, index) => (
                    <Box key={task.key} direction="row" align="center" border={index > 0 ? { side: 'top', color: 'border-weak' } : undefined} pad={index > 0 ? { top: 'xxsmall' } : undefined}>
                        <Row
                            event={{ section, element: task.key }}
                            onClick={task.onClick}
                            plain
                            $hover={color('hover-subtle')}
                        >
                            <Box direction="row" align={task.details ? 'start' : 'center'} gap="small" pad={{ vertical: '6px', horizontal: 'xsmall' }}>
                                {/* The accent circle Settings sections put their icon in, warm for what's stuck */}
                                <Icon
                                    SelectedIcon={task.icon}
                                    highlight="accent"
                                    highlightProps={task.attention ? { background: tint } : undefined}
                                    size="medium"
                                    iconContainerProps={{ margin: { vertical: '-6px' } }}
                                />
                                <Box flex style={{ minWidth: 0 }}>
                                    {/* A long email address breaks rather than pushing past the card */}
                                    <Text style={{ overflowWrap: 'anywhere' }}>{task.lead && <Text weight={600}>{task.lead} </Text>}{task.text}</Text>
                                    {task.details && (
                                        <Box margin={{ top: 'xxsmall' }}>
                                            {task.details.map((line) => <Text key={line} size="small" color="text-weak" truncate>{line}</Text>)}
                                        </Box>
                                    )}
                                </Box>
                                <Box direction="row" align="center" gap="xxsmall" flex={false} alignSelf="center">
                                    {!isPhone && <Text size="small" weight={500}>{task.action}</Text>}
                                    <Icon SelectedIcon={IconChevronRight} size="xsmall" />
                                </Box>
                            </Box>
                        </Row>
                    </Box>
                ))}
            </Box>
        </ChartCard>
    );
};

export default TaskList;
